import { useEffect, useRef, useState } from "react";
import playericon from "../assets/user.webp";
import { showToast } from "./ToastNotification";
import { useSelector } from "react-redux";

const UserPlayersAssignModal = ({
  team,
  selectedSubcategory,
  onClose,
  onAssigned,
}) => {
  const modalRef = useRef();
  const token = useSelector((s) => s.user.token);

  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  // paginación server-side
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);

  const [selected, setSelected] = useState([]);

  useEffect(() => {
    setPlayers([]); // si cambia search resetea
    setPage(1);
  }, [search]);

  useEffect(() => {
    fetchPlayers();
  }, [page, search]);

  const fetchPlayers = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await fetch(
        `https://server-postgre.vercel.app/api/players?search=${search}&page=${page}&limit=${limit}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (!res.ok) throw new Error("Error al obtener jugadores");

      const data = await res.json();

      // backend debe responder algo tipo { items, totalPages }
      setPlayers((prev) => [...prev, ...data.items]);
      setTotalPages(data.totalPages);
    } catch (err) {
      setError("No se pudieron cargar los jugadores.");
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (p) => {
    setSelected((prev) =>
      prev.includes(p.id) ? prev.filter((id) => id !== p.id) : [...prev, p.id],
    );
  };

  const batchAssign = async () => {
    if (!selected.length) return;

    const baseBody = {
      team_id: team.id,
      ...(selectedSubcategory?.id && {
        team_category_id: selectedSubcategory.id,
      }),
    };

    try {
      for (const player_id of selected) {
        await fetch(`https://server-postgre.vercel.app/api/players/assign`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ ...baseBody, player_id }),
        });
      }

      showToast.success("Jugadores asignados correctamente.");
      onAssigned();
      onClose();
    } catch (err) {
      showToast.error("Error al asignar jugadores.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div
        ref={modalRef}
        className="bg-white w-full max-w-lg max-h-[90vh] flex flex-col shadow-lg rounded-lg"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-4 py-3 flex justify-between items-center">
          <h2 className="text-lg font-semibold">Selecciona jugadores</h2>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-800 text-xl font-bold"
          >
            ✖
          </button>
        </div>

        {/* Search */}
        <div className="sticky top-[48px] bg-white border-b px-4 py-3">
          <input
            type="text"
            placeholder="Buscar jugador..."
            className="w-full p-2 border rounded-md"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {players.map((p) => {
            const checked = selected.includes(p.id);
            return (
              <div
                key={p.id}
                onClick={() => toggleSelect(p)}
                className="flex items-center gap-3 border rounded-lg px-3 py-2 cursor-pointer hover:bg-gray-50"
              >
                <input type="checkbox" readOnly checked={checked} />
                <img
                  src={p.profile_picture_url || playericon}
                  className="w-10 h-10 rounded-full"
                />
                <div className="flex flex-col">
                  <span className="font-medium text-gray-800">
                    {p.first_name} {p.last_name}
                  </span>
                  <span className="text-xs text-gray-500">{p.player_type}</span>
                </div>
              </div>
            );
          })}

          {!loading && players.length === 0 && (
            <p className="text-center text-gray-500">Sin resultados.</p>
          )}

          {error && <p className="text-center text-red-500">{error}</p>}

          {page < totalPages && (
            <button
              onClick={() => setPage((p) => p + 1)}
              className="w-full py-2 border rounded hover:bg-gray-100"
            >
              Cargar más
            </button>
          )}

          {loading && <p className="text-center text-gray-500">Cargando...</p>}
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-between">
          <button
            onClick={onClose}
            className="px-3 py-2 bg-gray-200 rounded hover:bg-gray-300"
          >
            Cancelar
          </button>
          <button
            onClick={batchAssign}
            className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Registrar ({selected.length})
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserPlayersAssignModal;
